/**
 * PRD P0/P1 补齐页面的 mock 数据集
 *
 * 数据红线遵循：
 * - 金额字段统一 string 类型（PRD 规定 decimal(18,2) 禁 double）
 * - 价差/结算相关数据严禁出现交易笔数/GMV/持卡人（UI-R01 / ACC-008 / FIN-002）
 * - 卡号统一掩码格式 **** **** **** 后四位（禁完整 PAN 默认展示）
 * - PII（邮箱/手机/姓名）需脱敏
 */
import { maskPan, maskEmail, maskPhone, maskName } from '@/lib/format';

// =========================================================================
// 一、账户中心 — 开户审核（ACC-001~003/005 共用）
// =========================================================================
export type OnboardingType = 'AGENT_ONBOARD' | 'MERCHANT_ONBOARD' | 'MERCHANT_PROFILE_CHANGE';
export type ScreeningResult = 'PASS' | 'HIT' | 'PENDING';
/** DRAFT：两段式进件草稿态（未提交审核，不可审、不计入待审数） */
export type AuditStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPPLEMENT';

export interface OrigOnboardingApplication {
  applicationId: string;
  subjectName: string;
  type: OnboardingType;
  submittedAt: string;
  screeningResult: ScreeningResult;
  status: AuditStatus;
  agentName?: string;
  submitter: string;
  reviewer?: string;
  reviewedAt?: string;
  comment?: string;
  /** 申请单号别名（applicationNo） */
  applicationNo?: string;
  /** 提交来源：AGENT | MERCHANT_SELF | ADMIN */
  source?: string;
  /** 注册号 / 统一社会信用代码 */
  registrationNo?: string;
  /** 行业 MCC */
  /** 筛查命中标签（对齐后端 ScreeningHitType：SANCTIONS/BLACKLIST/NONE） */
  screeningTags?: string[];
  /** 当前认领人 ID（字符串化） */
  claimerId?: string;
  /** 队列停留时长（秒） */
  waitingSeconds?: number;
  /** MERCHANT_PROFILE_CHANGE 专用：字段级 Diff */
  kycDiff?: { field: string; fieldLabel: string; oldValue: string; newValue: string }[];
  /**
   * 绑定商户 id（W4-TD3 直查演示字段，对齐后端 onboarding_application.subject_id：
   * 开户 APPROVE 时 bindApprovedSubject / 资质变更草稿 assignSubject 才写入）。
   * 仅 mock 数据标注；PENDING/REJECTED 申请无绑定（subjectId 缺省），直查不命中属精确语义。
   */
  subjectId?: number | string;
}

export const onboardingApplications: OrigOnboardingApplication[] = [
  { applicationId: 'APP-2025-0001', applicationNo: 'APP-2025-0001', source: 'AGENT', subjectName: '环亚金融分销', type: 'AGENT_ONBOARD', submittedAt: '2025-07-28 09:30:00', screeningResult: 'PASS', screeningTags: ['NONE'], registrationNo: '91440300MA5E12K7XG', waitingSeconds: 3600, status: 'PENDING', submitter: '渠道 张经理' },
  { applicationId: 'APP-2025-0002', applicationNo: 'APP-2025-0002', source: 'AGENT', subjectName: '鼎丰支付服务', type: 'AGENT_ONBOARD', submittedAt: '2025-07-27 14:20:00', screeningResult: 'HIT', screeningTags: ['SANCTIONS'], registrationNo: '91440300MA5F71Q3XA', waitingSeconds: 72000, status: 'PENDING', submitter: '渠道 张经理', comment: '筛查命中 PEP 名单，需 L2 复核' },
  { applicationId: 'APP-2025-0003', applicationNo: 'APP-2025-0003', source: 'AGENT', subjectName: '环球购物服务', type: 'AGENT_ONBOARD', submittedAt: '2025-07-20 10:00:00', screeningResult: 'PASS', screeningTags: ['NONE'], registrationNo: '91440300MA5C6RT4KQ', waitingSeconds: 0, status: 'APPROVED', submitter: '渠道 张经理', reviewer: '合规 刘总监', reviewedAt: '2025-07-22 16:00:00' },

  { applicationId: 'APP-2025-0010', subjectName: '云闪付科技', type: 'MERCHANT_ONBOARD', submittedAt: '2025-07-29 11:00:00', screeningResult: 'PASS', status: 'PENDING', agentName: '汇通金融服务', submitter: '运营 王专员' },
  { applicationId: 'APP-2025-0011', subjectName: '跨境优品电商', type: 'MERCHANT_ONBOARD', submittedAt: '2025-07-28 16:30:00', screeningResult: 'HIT', status: 'PENDING', agentName: '星辰国际金融', submitter: '运营 王专员', comment: 'MCC=5967 高风险，需风控复核' },
  { applicationId: 'APP-2025-0012', subjectName: '新亚数字商城', type: 'MERCHANT_ONBOARD', submittedAt: '2025-07-25 09:00:00', screeningResult: 'PASS', status: 'APPROVED', agentName: '鼎盛金融集团', submitter: '运营 王专员', reviewer: '合规 刘总监', reviewedAt: '2025-07-26 14:00:00', subjectId: 1 },
  { applicationId: 'APP-2025-0013', subjectName: '速达物流支付', type: 'MERCHANT_ONBOARD', submittedAt: '2025-07-24 13:00:00', screeningResult: 'PASS', status: 'REJECTED', agentName: '汇通金融服务', submitter: '运营 王专员', reviewer: '运营 李主管', reviewedAt: '2025-07-24 17:00:00', comment: '账户名不一致' },

  { applicationId: 'APP-2025-0020', subjectName: '亿通数字服务', type: 'MERCHANT_PROFILE_CHANGE', submittedAt: '2025-07-29 10:00:00', screeningResult: 'PASS', status: 'PENDING', agentName: '星辰国际金融', submitter: '商户自助提交',
    kycDiff: [
      { field: 'businessScope', fieldLabel: '经营范围', oldValue: '电子商务', newValue: '电子商务；跨境电商' },
      { field: 'contactPhone', fieldLabel: '联系电话', oldValue: '+852 8234 5678', newValue: '+852 9000 1234' },
    ],
  },
  { applicationId: 'APP-2025-0021', subjectName: '泰和智能科技', type: 'MERCHANT_PROFILE_CHANGE', submittedAt: '2025-07-27 15:00:00', screeningResult: 'PASS', status: 'SUPPLEMENT', agentName: '鼎盛金融集团', submitter: '商户自助提交', reviewer: '运营 李主管', reviewedAt: '2025-07-28 10:00:00', comment: '需补充变更后营业执照扫描件' },
];

// =========================================================================
// 三、财务中心 — 商户对账单（FIN-001）
// =========================================================================
export interface OrigMerchantStatement {
  statementId: string;
  merchantName: string;
  merchantId: string;
  period: string;
  currency: string;
  amount: string; // decimal(18,2) string
  status: 'DRAFT' | 'ISSUED' | 'DISPUTED' | 'CLOSED';
  diffFlag: boolean;
  createdAt: string;
}

export const merchantStatements: OrigMerchantStatement[] = [
  { statementId: 'STMT-2025-07-001', merchantName: '环球购物服务', merchantId: 'MCH001', period: '2025-07', currency: 'USD', amount: '380000.00', status: 'ISSUED', diffFlag: false, createdAt: '2025-08-01 00:00:00' },
  { statementId: 'STMT-2025-07-002', merchantName: '亿通数字服务', merchantId: 'MCH002', period: '2025-07', currency: 'USD', amount: '210000.00', status: 'DISPUTED', diffFlag: true, createdAt: '2025-08-01 00:00:00' },
  { statementId: 'STMT-2025-07-003', merchantName: '泰和智能科技', merchantId: 'MCH003', period: '2025-07', currency: 'USD', amount: '0.00', status: 'DRAFT', diffFlag: false, createdAt: '2025-08-01 00:00:00' },
  { statementId: 'STMT-2025-06-001', merchantName: '环球购物服务', merchantId: 'MCH001', period: '2025-06', currency: 'USD', amount: '350000.00', status: 'CLOSED', diffFlag: false, createdAt: '2025-07-01 00:00:00' },
];

// =========================================================================
// 四、财务中心 — 代理商价差收益结算（FIN-002）
// 数据红线：严禁交易笔数/GMV/持卡人，仅利润金额
// =========================================================================
export interface OrigAgentSpreadSettlement {
  batchId: string;
  agentName: string;
  agentId: string;
  spreadAmount: string; // decimal(18,2) string —— 仅价差利润
  status: 'PENDING' | 'APPROVED' | 'PAID' | 'FAILED';
  period: string;
  paidAt?: string;
}

export const agentSpreadSettlements: OrigAgentSpreadSettlement[] = [
  { batchId: 'SPD-2025-07-AGT001', agentName: '星辰国际金融', agentId: 'AGT001', spreadAmount: '14353.00', status: 'PENDING', period: '2025-07' },
  { batchId: 'SPD-2025-07-AGT002', agentName: '汇通金融服务', agentId: 'AGT002', spreadAmount: '12800.00', status: 'APPROVED', period: '2025-07' },
  { batchId: 'SPD-2025-07-AGT006', agentName: '鼎盛金融集团', agentId: 'AGT006', spreadAmount: '32000.00', status: 'PAID', period: '2025-07', paidAt: '2025-08-01 10:00:00' },
  { batchId: 'SPD-2025-06-AGT001', agentName: '星辰国际金融', agentId: 'AGT001', spreadAmount: '12100.00', status: 'PAID', period: '2025-06', paidAt: '2025-07-01 10:00:00' },
];

// =========================================================================
// 五、财务中心 — 强制退款/赔付（FIN-007 D-S22）
// 数据红线：科目仅 CHANNEL_REFUND / PLATFORM_OPEX，禁止用户资金池垫付
// =========================================================================
export interface OrigForceRefund {
  refundId: string;
  userMask: string;
  amount: string;
  ledgerSubject: 'CHANNEL_REFUND' | 'PLATFORM_OPEX';
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'POSTED' | 'REJECTED';
  ticketId: string;
  reason: string;
  createdAt: string;
}

export const forceRefunds: OrigForceRefund[] = [
  { refundId: 'FR-2025-0001', userMask: maskName('张小明') + ' / ****1234', amount: '299.99', ledgerSubject: 'CHANNEL_REFUND', status: 'PENDING_APPROVAL', ticketId: 'TKT-9001', reason: '商户误扣款，客户投诉并提供凭证', createdAt: '2025-07-29 14:00:00' },
  { refundId: 'FR-2025-0002', userMask: maskName('李华') + ' / ****5678', amount: '1500.00', ledgerSubject: 'PLATFORM_OPEX', status: 'PENDING_APPROVAL', ticketId: 'TKT-9000', reason: '系统故障导致重复扣款，平台承担', createdAt: '2025-07-28 16:00:00' },
  { refundId: 'FR-2025-0003', userMask: maskName('王芳') + ' / ****9012', amount: '89.99', ledgerSubject: 'CHANNEL_REFUND', status: 'POSTED', ticketId: 'TKT-8998', reason: '未收到商品，争议成立', createdAt: '2025-07-27 10:00:00' },
  { refundId: 'FR-2025-0004', userMask: maskName('赵磊') + ' / ****3456', amount: '500.00', ledgerSubject: 'PLATFORM_OPEX', status: 'REJECTED', ticketId: 'TKT-8997', reason: '超阈值需 L3 会签', createdAt: '2025-07-26 11:00:00' },
];

// =========================================================================
// 六、财务中心 — 资金调拨（FIN-003）
// =========================================================================
export interface OrigFundTransfer {
  transferId: string;
  fromAccount: string;
  toAccount: string;
  amount: string;
  currency: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REJECTED';
  createdBy: string;
  reason?: string;
  createdAt: string;
}

export const fundTransfers: OrigFundTransfer[] = [
  { transferId: 'TRF-2025-0001', fromAccount: '平台备付金 USD', toAccount: 'Visa 备付金 USD', amount: '500000.00', currency: 'USD', status: 'PENDING_APPROVAL', createdBy: '财务 王总', reason: 'Visa 季度备付金补足', createdAt: '2025-07-29 10:00:00' },
  { transferId: 'TRF-2025-0002', fromAccount: 'USDT 热钱包', toAccount: '交易所中转', amount: '100000.00', currency: 'USDT', status: 'COMPLETED', createdBy: '财务 李总', reason: 'USDT 流动性调拨', createdAt: '2025-07-28 14:00:00' },
  { transferId: 'TRF-2025-0003', fromAccount: '平台备付金 USD', toAccount: 'Mastercard 结算户', amount: '300000.00', currency: 'USD', status: 'PROCESSING', createdBy: '财务 王总', reason: 'MC 清算资金', createdAt: '2025-07-29 11:00:00' },
];

// =========================================================================
// 八、财务中心 — 结算批次（FIN-006 重构对齐字段）
// =========================================================================
export interface OrigSettlementBatch {
  batchId: string;
  batchType: 'MERCHANT_SETTLE' | 'AGENT_SPREAD' | 'CHANNEL_CLEAR';
  amount: string;
  currency: string;
  status: 'OPEN' | 'REVIEW' | 'SETTLED' | 'FAILED';
  createdAt: string;
  settledAt?: string;
}

export const settlementBatches: OrigSettlementBatch[] = [
  { batchId: 'BATCH-2025-0731-M', batchType: 'MERCHANT_SETTLE', amount: '1250000.00', currency: 'USD', status: 'REVIEW', createdAt: '2025-07-31 23:59:00' },
  { batchId: 'BATCH-2025-0731-A', batchType: 'AGENT_SPREAD', amount: '89000.00', currency: 'USD', status: 'SETTLED', createdAt: '2025-07-31 23:59:00', settledAt: '2025-08-01 10:00:00' },
  { batchId: 'BATCH-2025-0730-C', batchType: 'CHANNEL_CLEAR', amount: '2100000.00', currency: 'USD', status: 'SETTLED', createdAt: '2025-07-30 23:59:00', settledAt: '2025-07-31 08:00:00' },
  { batchId: 'BATCH-2025-0729-M', batchType: 'MERCHANT_SETTLE', amount: '980000.00', currency: 'USD', status: 'FAILED', createdAt: '2025-07-29 23:59:00' },
];

// =========================================================================
// 九、财务中心 — 多币种头寸（FIN-004 重构补 alertLevel）
// =========================================================================
export interface OrigTreasuryPosition {
  currency: string;
  available: string;
  inTransit: string;
  alertLevel: 'OK' | 'WARN' | 'CRIT';
  safetyLine: string;
  updatedAt: string;
}

export const treasuryPositions: OrigTreasuryPosition[] = [
  { currency: 'USD', available: '4500000.00', inTransit: '500000.00', alertLevel: 'OK', safetyLine: '1000000.00', updatedAt: '2025-07-29 12:00:00' },
  { currency: 'HKD', available: '8200000.00', inTransit: '300000.00', alertLevel: 'OK', safetyLine: '2000000.00', updatedAt: '2025-07-29 12:00:00' },
  { currency: 'USDT', available: '180000.00', inTransit: '50000.00', alertLevel: 'WARN', safetyLine: '200000.00', updatedAt: '2025-07-29 12:00:00' },
  { currency: 'EUR', available: '95000.00', inTransit: '20000.00', alertLevel: 'CRIT', safetyLine: '150000.00', updatedAt: '2025-07-29 12:00:00' },
];

// =========================================================================
// 十、卡片中心 — 全局卡片总览（CARD-001）
// =========================================================================
export const cardOverview = {
  totalCards: 15890,
  activeCards: 12340,
  frozenCards: 890,
  abnormalCards: 156,
  physicalStock: 3200,
  freezeRate: 5.6, // >5% 标红
  wowChange: 2.3,
  refreshedAt: '2025-07-29 12:00:00',
};

// =========================================================================
// 十一、卡片中心 — 卡号精准检索（CARD-002）
// =========================================================================
export interface OrigCardRecord {
  cardId: string;
  panMasked: string; // **** **** **** 后四位
  cardType: 'VIRTUAL' | 'PHYSICAL';
  holderName: string; // 脱敏
  merchantName: string;
  status: 'ACTIVE' | 'FROZEN' | 'CLOSED' | 'PENDING_OPEN';
  balance: string;
  lastTxAt?: string;
  limitPerTxn: string;
  limitDaily: string;
}

export const cardSearchResults: OrigCardRecord[] = [
  { cardId: 'CARD-00000001', panMasked: maskPan('4111111111111234'), cardType: 'VIRTUAL', holderName: maskName('张小明'), merchantName: '环球购物服务', status: 'ACTIVE', balance: '5000.00', lastTxAt: '2025-07-29 10:00:00', limitPerTxn: '2000.00', limitDaily: '10000.00' },
  { cardId: 'CARD-00000002', panMasked: maskPan('5241888800005678'), cardType: 'VIRTUAL', holderName: maskName('李华'), merchantName: '亿通数字服务', status: 'FROZEN', balance: '3200.00', lastTxAt: '2025-07-28 14:00:00', limitPerTxn: '1500.00', limitDaily: '8000.00' },
  { cardId: 'CARD-00000003', panMasked: maskPan('4111222233339012'), cardType: 'PHYSICAL', holderName: maskName('王芳'), merchantName: '环球购物服务', status: 'ACTIVE', balance: '12000.00', lastTxAt: '2025-07-29 09:00:00', limitPerTxn: '5000.00', limitDaily: '20000.00' },
  { cardId: 'CARD-00000004', panMasked: maskPan('5241000099883456'), cardType: 'VIRTUAL', holderName: maskName('赵磊'), merchantName: '泰和智能科技', status: 'CLOSED', balance: '0.00', lastTxAt: '2025-06-15 11:00:00', limitPerTxn: '0.00', limitDaily: '0.00' },
];

// =========================================================================
// 十二、卡片中心 — 卡片交易流水（CARD-005 / CARD-009）
// =========================================================================
export interface OrigCardTransaction {
  txId: string;
  txAt: string;
  amount: string;
  currency: string;
  merchantName: string;
  holderMasked: string;
  panMasked: string;
  status: 'SUCCESS' | 'FAILED' | 'REVERSED' | 'PENDING';
  mcc: string;
  channelCode: string;
}

export const cardTransactions: OrigCardTransaction[] = [
  { txId: 'TX-2025-000001', txAt: '2025-07-29 10:23:14', amount: '89.99', currency: 'USD', merchantName: 'Amazon US', holderMasked: maskName('张小明'), panMasked: maskPan('4111111111111234'), status: 'SUCCESS', mcc: '5732', channelCode: 'VISA' },
  { txId: 'TX-2025-000002', txAt: '2025-07-29 09:00:00', amount: '15.99', currency: 'USD', merchantName: 'Netflix', holderMasked: maskName('张小明'), panMasked: maskPan('4111111111111234'), status: 'SUCCESS', mcc: '4899', channelCode: 'VISA' },
  { txId: 'TX-2025-000003', txAt: '2025-07-28 18:00:00', amount: '999.00', currency: 'USD', merchantName: 'Apple Store', holderMasked: maskName('张小明'), panMasked: maskPan('4111111111111234'), status: 'SUCCESS', mcc: '5732', channelCode: 'VISA' },
  { txId: 'TX-2025-000004', txAt: '2025-07-28 14:20:00', amount: '2500.00', currency: 'USD', merchantName: '奢侈品商城', holderMasked: maskName('李华'), panMasked: maskPan('5241888800005678'), status: 'FAILED', mcc: '5967', channelCode: 'MC' },
  { txId: 'TX-2025-000005', txAt: '2025-07-28 11:00:00', amount: '320.00', currency: 'USD', merchantName: '航空公司订票', holderMasked: maskName('王芳'), panMasked: maskPan('4111222233339012'), status: 'REVERSED', mcc: '4511', channelCode: 'VISA' },
];

// =========================================================================
// 十三、卡片中心 — 制卡批次追踪（CARD-007）
// =========================================================================
export interface OrigPhysicalCardBatch {
  batchNo: string;
  productName: string;
  qty: number;
  status: 'PENDING' | 'MAKING' | 'QC' | 'SHIPPING' | 'ARRIVED';
  vendor: string;
  cost: string;
  updatedAt: string;
}

export const physicalCardBatches: OrigPhysicalCardBatch[] = [
  { batchNo: 'PB-2025-0001', productName: 'VISA 实体卡 Premium', qty: 5000, status: 'QC', vendor: '金雅拓', cost: '2.50', updatedAt: '2025-07-28 10:00:00' },
  { batchNo: 'PB-2025-0002', productName: 'MC 实体卡 Standard', qty: 10000, status: 'MAKING', vendor: '捷德', cost: '1.80', updatedAt: '2025-07-27 14:00:00' },
  { batchNo: 'PB-2025-0003', productName: 'VISA 实体卡 Basic', qty: 3000, status: 'SHIPPING', vendor: '金雅拓', cost: '1.50', updatedAt: '2025-07-29 09:00:00' },
  { batchNo: 'PB-2025-0004', productName: 'MC 实体卡 Premium', qty: 2000, status: 'ARRIVED', vendor: '捷德', cost: '2.20', updatedAt: '2025-07-25 16:00:00' },
];

// =========================================================================
// 十四、运营中心 — 入站邮件拦截（OPS-004b）
// =========================================================================
export interface OrigInboundEmail {
  emailId: string;
  fromAddr: string;
  subject: string;
  parseStatus: 'MATCHED' | 'UNMATCHED' | 'ERROR';
  rewriteStatus: 'PENDING' | 'REWRITTEN' | 'SENT' | 'DEAD';
  receivedAt: string;
  matchedRule?: string;
}

export const inboundEmails: OrigInboundEmail[] = [
  { emailId: 'IEMAIL-0001', fromAddr: maskEmail('noreply@allinpay.com'), subject: '【通联支付】您的验证码', parseStatus: 'MATCHED', rewriteStatus: 'REWRITTEN', receivedAt: '2025-07-29 10:00:00', matchedRule: 'Allinpay-3DS-OTP' },
  { emailId: 'IEMAIL-0002', fromAddr: maskEmail('security@visa-net.com'), subject: '3DS Challenge Notification', parseStatus: 'UNMATCHED', rewriteStatus: 'PENDING', receivedAt: '2025-07-29 09:30:00' },
  { emailId: 'IEMAIL-0003', fromAddr: maskEmail('system@unknown.com'), subject: '验证邮件', parseStatus: 'ERROR', rewriteStatus: 'DEAD', receivedAt: '2025-07-28 16:00:00' },
  { emailId: 'IEMAIL-0004', fromAddr: maskEmail('noreply@allinpay.com'), subject: '【通联】交易确认', parseStatus: 'MATCHED', rewriteStatus: 'SENT', receivedAt: '2025-07-28 14:00:00', matchedRule: 'Allinpay-3DS-OTP' },
];

export interface OrigEmailParseRule {
  ruleId: string;
  name: string;
  senderPattern: string;
  subjectPattern: string;
  enabled: boolean;
  rewriteBrand: string;
}

export const emailParseRules: OrigEmailParseRule[] = [
  { ruleId: 'RULE-001', name: '通联 3DS OTP', senderPattern: '*@allinpay.com', subjectPattern: '验证码*', enabled: true, rewriteBrand: 'Allinks' },
  { ruleId: 'RULE-002', name: 'Visa 网络通知', senderPattern: '*@visa-net.com', subjectPattern: '3DS*', enabled: true, rewriteBrand: 'Allinks' },
];

// =========================================================================
// 十五、运营中心 — 驾驶舱指标（OPS-008）
// =========================================================================
export interface OrigCockpitMetric {
  metricCode: string;
  metricName: string;
  value: number;
  unit: string;
  wow: number;
  alert: 'OK' | 'WARN' | 'CRIT';
}

export const cockpitMetrics: OrigCockpitMetric[] = [
  { metricCode: 'ACTIVE_CARDS', metricName: '活跃卡片数', value: 12340, unit: '张', wow: 2.3, alert: 'OK' },
  { metricCode: 'TXN_SUCCESS_RATE', metricName: '交易成功率', value: 99.2, unit: '%', wow: 0.1, alert: 'OK' },
  { metricCode: 'DEPOSIT_PENDING', metricName: '入金待审', value: 12, unit: '笔', wow: -15.0, alert: 'OK' },
  { metricCode: 'FREEZE_RATE', metricName: '冻结率', value: 5.6, unit: '%', wow: 0.4, alert: 'WARN' },
  { metricCode: 'DECLINE_RATE', metricName: '拒付率', value: 1.8, unit: '%', wow: 0.3, alert: 'WARN' },
  { metricCode: 'SYSTEM_LATENCY', metricName: '系统平均延迟', value: 320, unit: 'ms', wow: 5.0, alert: 'CRIT' },
];

// =========================================================================
// 十六、运营中心 — 配置中心（OPS-009）
// =========================================================================
export interface OrigOpsConfig {
  configKey: string;
  valuePreview: string; // 密钥脱敏
  env: 'dev' | 'staging' | 'prod';
  status: 'ACTIVE' | 'INACTIVE';
  updatedBy: string;
  updatedAt: string;
  sensitive: boolean;
}

export const opsConfigs: OrigOpsConfig[] = [
  { configKey: 'payment.gateway.timeout', valuePreview: '30000', env: 'prod', status: 'ACTIVE', updatedBy: 'SRE 陈工', updatedAt: '2025-07-28 10:00:00', sensitive: false },
  { configKey: 'risk.fraud.threshold', valuePreview: '0.85', env: 'prod', status: 'ACTIVE', updatedBy: '风控 刘主管', updatedAt: '2025-07-27 14:00:00', sensitive: false },
  { configKey: 'channel.allinpay.api_key', valuePreview: 'sk-***...***', env: 'prod', status: 'ACTIVE', updatedBy: 'SRE 陈工', updatedAt: '2025-07-20 09:00:00', sensitive: true },
  { configKey: 'email.smtp.password', valuePreview: '••••••••', env: 'prod', status: 'ACTIVE', updatedBy: 'SRE 陈工', updatedAt: '2025-07-15 11:00:00', sensitive: true },
];

// =========================================================================
// 十七、运营中心 — 监控告警（OPS-010）
// =========================================================================
export interface OrigSreAlert {
  alertId: string;
  service: string;
  severity: 'P0' | 'P1' | 'P2' | 'P3';
  status: 'FIRING' | 'ACK' | 'RESOLVED';
  firedAt: string;
  message: string;
  assignee?: string;
}

export const sreAlerts: OrigSreAlert[] = [
  { alertId: 'ALT-0001', service: 'payment-gateway', severity: 'P0', status: 'FIRING', firedAt: '2025-07-29 11:50:00', message: '支付网关 5xx 错误率 > 5%' },
  { alertId: 'ALT-0002', service: 'card-service', severity: 'P1', status: 'ACK', firedAt: '2025-07-29 10:30:00', message: '开卡接口延迟 P99 > 2s', assignee: 'SRE 陈工' },
  { alertId: 'ALT-0003', service: 'risk-engine', severity: 'P2', status: 'FIRING', firedAt: '2025-07-29 09:00:00', message: '风控规则命中率异常下降' },
  { alertId: 'ALT-0004', service: 'deposit-service', severity: 'P1', status: 'RESOLVED', firedAt: '2025-07-28 16:00:00', message: '入金审核队列堆积 > 50', assignee: 'SRE 陈工' },
];

// =========================================================================
// 十八、报表中心 — 导出任务（RPT-003）
// =========================================================================
export interface OrigReportExportJob {
  jobId: string;
  reportName: string;
  format: 'CSV' | 'XLSX' | 'PDF';
  status: 'QUEUED' | 'GENERATING' | 'DONE' | 'FAILED';
  createdAt: string;
  createdBy: string;
  maskPolicy: 'NONE' | 'MASK_PAN' | 'MASK_PII';
}

export const reportExportJobs: OrigReportExportJob[] = [
  { jobId: 'EXP-2025-0001', reportName: '7 月商户交易月报', format: 'XLSX', status: 'DONE', createdAt: '2025-07-29 10:00:00', createdBy: '分析师 周理', maskPolicy: 'MASK_PAN' },
  { jobId: 'EXP-2025-0002', reportName: '代理商价差结算汇总', format: 'CSV', status: 'GENERATING', createdAt: '2025-07-29 11:00:00', createdBy: '财务 王总', maskPolicy: 'MASK_PII' },
  { jobId: 'EXP-2025-0003', reportName: '风险预警周报', format: 'PDF', status: 'QUEUED', createdAt: '2025-07-29 11:30:00', createdBy: '风控 刘主管', maskPolicy: 'NONE' },
  { jobId: 'EXP-2025-0004', reportName: 'Q2 财务年报', format: 'PDF', status: 'FAILED', createdAt: '2025-07-28 14:00:00', createdBy: '分析师 周理', maskPolicy: 'MASK_PAN' },
];

// =========================================================================
// 十九、报表中心 — 权限与脱敏 ACL（RPT-004）
// =========================================================================
export interface OrigReportAcl {
  reportCode: string;
  reportName: string;
  roleCode: string;
  roleName: string;
  visibleCols: string[];
  maskPolicy: 'NONE' | 'MASK_PAN' | 'MASK_PII';
  updatedAt: string;
}

export const reportAcls: OrigReportAcl[] = [
  { reportCode: 'RPT-MERCHANT-TXN', reportName: '商户交易月报', roleCode: 'PLT_OPS', roleName: '平台运营', visibleCols: ['merchantName', 'txnCount', 'amount', 'status'], maskPolicy: 'MASK_PAN', updatedAt: '2025-07-28 10:00:00' },
  { reportCode: 'RPT-MERCHANT-TXN', reportName: '商户交易月报', roleCode: 'PLT_FINANCE', roleName: '平台财务', visibleCols: ['merchantName', 'txnCount', 'amount', 'fee', 'status'], maskPolicy: 'MASK_PAN', updatedAt: '2025-07-28 10:00:00' },
  { reportCode: 'RPT-AGENT-SPREAD', reportName: '代理商价差汇总', roleCode: 'PLT_FINANCE', roleName: '平台财务', visibleCols: ['agentName', 'spreadAmount', 'period'], maskPolicy: 'MASK_PII', updatedAt: '2025-07-27 14:00:00' },
  // 数据红线：代理商报表禁交易本金列（UI-R01 reject）
  { reportCode: 'RPT-AGENT-SPREAD', reportName: '代理商价差汇总', roleCode: 'PLT_OPS', roleName: '平台运营', visibleCols: ['agentName', 'spreadAmount', 'period'], maskPolicy: 'MASK_PII', updatedAt: '2025-07-27 14:00:00' },
];

// =========================================================================
// 二十、报表中心 — 预置模板（RPT-001）/ 订阅（RPT-002）/ 评论（RPT-005）/ 异常（RPT-006）
// =========================================================================
export const reportTemplates = [
  { templateId: 'TPL-001', name: '商户交易月报', category: 'finance', roles: ['PLT_OPS', 'PLT_FINANCE'], updatedAt: '2025-07-01 00:00:00' },
  { templateId: 'TPL-002', name: '代理商价差汇总', category: 'finance', roles: ['PLT_FINANCE'], updatedAt: '2025-07-01 00:00:00' },
  { templateId: 'TPL-003', name: '风险预警周报', category: 'risk', roles: ['PLT_RISK'], updatedAt: '2025-07-01 00:00:00' },
  { templateId: 'TPL-004', name: '卡片运营日报', category: 'card', roles: ['PLT_OPS'], updatedAt: '2025-07-01 00:00:00' },
];

export const reportSubscriptions = [
  { subId: 'SUB-001', reportName: '商户交易月报', cron: '0 0 1 * *', channel: 'EMAIL', status: 'ACTIVE', recipient: maskEmail('finance@allinks.io') },
  { subId: 'SUB-002', reportName: '风险预警周报', cron: '0 9 * * 1', channel: 'SMS', status: 'ACTIVE', recipient: maskPhone('+852 98765432') },
  { subId: 'SUB-003', reportName: '卡片运营日报', cron: '0 9 * * *', channel: 'EMAIL', status: 'INACTIVE', recipient: maskEmail('ops@allinks.io') },
];

export const reportCollabs = [
  { noteId: 'NOTE-001', reportName: '商户交易月报', author: '分析师 周理', status: 'OPEN', content: '7 月 MCH002 交易量异常下降 30%，建议核查', createdAt: '2025-07-29 10:00:00' },
  { noteId: 'NOTE-002', reportName: '代理商价差汇总', author: '财务 王总', status: 'RESOLVED', content: 'AGT006 价差异常已确认是阶梯分润调整', createdAt: '2025-07-28 14:00:00' },
];

export const reportAnomalies = [
  { annId: 'ANN-001', reportName: '商户交易月报', metric: 'GMV_WOW', delta: -30.0, rootCause: 'MCH002 商户被限额调整导致交易量下降', at: '2025-07-29 00:00:00', status: 'OPEN' },
  { annId: 'ANN-002', reportName: '卡片运营日报', metric: 'DECLINE_RATE', delta: 0.8, rootCause: '某通道临时故障导致拒付率上升', at: '2025-07-28 00:00:00', status: 'RESOLVED' },
  { annId: 'ANN-003', reportName: '卡片运营日报', metric: 'FREEZE_RATE', delta: 0.4, rootCause: '风控规则升级触发批量冻结', at: '2025-07-27 00:00:00', status: 'IGNORED' },
];
